// The back of an achievement track card: the track's description, then its
// earned levels and the current one, newest first.

import React from 'react';
import { CheckIcon } from 'lucide-react';
import type { AchievementTrackProgress } from '@airhead-games/shared/types';
import { CompletedChip, RewardChip } from './AchievementChips';
import { isOneTime } from './achievementTrack';
import { trackPaletteStyle } from './trackPalette';

const formatDate = (value: Date | string): string =>
  new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });

const AchievementHistory: React.FC<{ track: AchievementTrackProgress }> = ({
  track,
}) => {
  const oneTime = isOneTime(track);
  const rows = track.milestones
    .slice(0, track.level)
    .map((milestone, index) => ({ milestone, level: index + 1 }))
    .reverse();

  return (
    <>
      <p className="min-h-8 pr-10 text-sm text-muted-foreground">
        {track.description}
      </p>
      <ol className="-mr-2 mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-2">
        {rows.map(({ milestone, level }) => (
          <li key={milestone.milestoneKey} className="flex items-center gap-3">
            <span
              style={trackPaletteStyle(track.accent, level)}
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--lvl-500) text-sm font-bold text-(--lvl-950)"
            >
              {oneTime ? (
                <CheckIcon className="size-4" strokeWidth={3} />
              ) : (
                level
              )}
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">
                {milestone.goal}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {milestone.completion
                  ? formatDate(milestone.completion.processedAt)
                  : `${track.value.toLocaleString()} / ${milestone.threshold.toLocaleString()}`}
              </span>
            </div>
            {milestone.completion ? (
              <CompletedChip />
            ) : (
              <RewardChip
                reward={milestone.reward}
                accent={track.accent}
                level={level}
              />
            )}
          </li>
        ))}
      </ol>
    </>
  );
};

export default AchievementHistory;
